import { useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, MapPin, Mail, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { CONTACTS, LOCATIONS, EMAIL, ENQUIRY_SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { name: "", phone: "", email: "", service: "", location: "", message: "" };

const validate = (form) => {
    const errors = {};
    if (form.name.trim().length < 2) errors.name = "Please enter your name.";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 15)
        errors.phone = "Please enter a valid phone number.";
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))
        errors.email = "Please enter a valid email address.";
    if (!form.service) errors.service = "Please select a service.";
    if (form.location.trim().length < 2) errors.location = "Please tell us the location.";
    if (form.message.trim().length < 10)
        errors.message = "Please share a few details (at least 10 characters).";
    return errors;
};

const inputClass =
    "w-full border border-charcoal/20 bg-cream px-4 py-3 text-sm text-ink placeholder:text-stone2/50 transition-colors duration-300 focus:border-bronze focus:outline-none";

const Contact = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState("");
    const [success, setSuccess] = useState(false);

    const update = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        setErrors((err) => ({ ...err, [field]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setServerError("");
        const validation = validate(form);
        setErrors(validation);
        if (Object.keys(validation).length > 0) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiry`, {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim() || null,
                service: form.service,
                location: form.location.trim(),
                message: form.message.trim(),
            });
            setSuccess(true);
        } catch (err) {
            setServerError(
                err.response?.status === 422
                    ? "Please review the highlighted fields and try again."
                    : "Something went wrong while sending your enquiry. Please call or WhatsApp us directly.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <Seo
                title="Contact Kavraj Spaces | Discuss a Requirement — New Delhi"
                description="Speak directly with the founders of Kavraj Spaces. Call or WhatsApp Yuvraj Singh Marwaha or Kanav Bakshi, or send an enquiry — Anand Niketan and Jail Road, New Delhi."
            />

            <PageHero
                eyebrow="Contact"
                title="Discuss a requirement."
                intro="Call, WhatsApp, or send a short brief below — you will be speaking directly with a founder, not a call centre."
                testid="contact-hero"
            />

            <section className="bg-cream" data-testid="contact-direct">
                <div className="mx-auto max-w-7xl px-6 py-16 sm:px-12 lg:px-20 lg:py-24">
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {CONTACTS.map((c, i) => (
                            <Reveal key={c.id} delay={i * 120}>
                                <div
                                    className="flex h-full flex-col border border-bronze/20 bg-cream-muted p-8 sm:p-10"
                                    data-testid={`contact-card-${c.id}`}
                                >
                                    <h2 className="font-serif text-2xl text-ink sm:text-3xl">{c.name}</h2>
                                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-bronze">
                                        {c.role}{c.focus ? ` — ${c.focus}` : ""}
                                    </p>
                                    <p className="mt-6 font-serif text-2xl tracking-wide text-ink">
                                        {c.phoneDisplay}
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-4">
                                        <a
                                            href={c.tel}
                                            data-testid={`contact-call-${c.id}`}
                                            className="inline-flex items-center gap-2 bg-bronze px-6 py-3 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-bronze-light"
                                        >
                                            <Phone size={16} aria-hidden="true" />
                                            Call now
                                        </a>
                                        <a
                                            href={c.whatsapp}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`contact-whatsapp-${c.id}`}
                                            className="inline-flex items-center gap-2 border border-charcoal/30 px-6 py-3 text-sm font-semibold tracking-wide text-charcoal transition-colors duration-300 hover:border-bronze hover:text-bronze"
                                        >
                                            <MessageCircle size={16} aria-hidden="true" />
                                            WhatsApp
                                        </a>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={100}>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="contact-email"
                            className="group mt-8 flex items-center justify-between gap-4 border border-bronze/20 bg-cream-muted p-8 transition-colors duration-300 hover:border-bronze/50 sm:p-10"
                        >
                            <div className="flex items-center gap-4">
                                <Mail size={26} className="shrink-0 text-bronze" aria-hidden="true" />
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                                        Prefer email?
                                    </p>
                                    <p className="mt-2 font-serif text-xl text-ink sm:text-2xl">{EMAIL}</p>
                                </div>
                            </div>
                            <span className="hidden text-sm font-semibold tracking-wide text-bronze transition-colors duration-300 group-hover:text-bronze-muted sm:block">
                                Write to us
                            </span>
                        </a>
                    </Reveal>

                    <Reveal delay={150}>
                        <div
                            className="mt-8 border border-bronze/20 bg-charcoal p-8 sm:p-10"
                            data-testid="contact-locations"
                        >
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze-light">
                                Where to find us
                            </p>
                            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
                                {LOCATIONS.map((loc) => (
                                    <div key={loc} className="flex items-start gap-3">
                                        <MapPin
                                            size={18}
                                            className="mt-1 shrink-0 text-bronze-light"
                                            aria-hidden="true"
                                        />
                                        <p className="font-serif text-xl text-cream">{loc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-bronze/15 bg-cream-muted" data-testid="contact-form-section">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
                        <Reveal className="lg:col-span-1">
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                                Send an enquiry
                            </p>
                            <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                                A short brief is enough to begin.
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-stone2">
                                Tell us what you are planning and where. A founder will call you
                                back — usually the same day.
                            </p>
                        </Reveal>

                        <Reveal delay={120} className="lg:col-span-2">
                            {success ? (
                                <div
                                    className="flex h-full flex-col items-start justify-center border border-bronze/30 bg-cream p-10"
                                    data-testid="enquiry-success"
                                >
                                    <CheckCircle2 size={40} className="text-bronze" aria-hidden="true" />
                                    <h3 className="mt-6 font-serif text-3xl text-ink">
                                        Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                                    </h3>
                                    <p className="mt-4 max-w-md text-base leading-relaxed text-stone2">
                                        Your enquiry has been received. Yuvraj or Kanav will reach
                                        out to you shortly. If it is urgent, call or WhatsApp either
                                        number above.
                                    </p>
                                    <button
                                        type="button"
                                        data-testid="enquiry-another-btn"
                                        onClick={() => {
                                            setForm(EMPTY_FORM);
                                            setSuccess(false);
                                        }}
                                        className="mt-8 border border-charcoal/30 px-6 py-3 text-sm font-semibold tracking-wide text-charcoal transition-colors duration-300 hover:border-bronze hover:text-bronze"
                                    >
                                        Send another enquiry
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    noValidate
                                    data-testid="enquiry-form"
                                    className="border border-bronze/20 bg-cream p-8 sm:p-10"
                                >
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="enquiry-name" className="mb-2 block text-sm font-medium text-ink">
                                                Full name <span className="text-bronze">*</span>
                                            </label>
                                            <input
                                                id="enquiry-name"
                                                data-testid="enquiry-name-input"
                                                type="text"
                                                value={form.name}
                                                onChange={update("name")}
                                                placeholder="Your name"
                                                aria-invalid={!!errors.name}
                                                aria-describedby={errors.name ? "error-name" : undefined}
                                                className={inputClass}
                                            />
                                            {errors.name && (
                                                <p id="error-name" data-testid="error-name" className="mt-2 text-xs text-red-700">
                                                    {errors.name}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="enquiry-phone" className="mb-2 block text-sm font-medium text-ink">
                                                Phone <span className="text-bronze">*</span>
                                            </label>
                                            <input
                                                id="enquiry-phone"
                                                data-testid="enquiry-phone-input"
                                                type="tel"
                                                value={form.phone}
                                                onChange={update("phone")}
                                                placeholder="+91 …"
                                                aria-invalid={!!errors.phone}
                                                aria-describedby={errors.phone ? "error-phone" : undefined}
                                                className={inputClass}
                                            />
                                            {errors.phone && (
                                                <p id="error-phone" data-testid="error-phone" className="mt-2 text-xs text-red-700">
                                                    {errors.phone}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="enquiry-email" className="mb-2 block text-sm font-medium text-ink">
                                                Email <span className="text-stone2/70">(optional)</span>
                                            </label>
                                            <input
                                                id="enquiry-email"
                                                data-testid="enquiry-email-input"
                                                type="email"
                                                value={form.email}
                                                onChange={update("email")}
                                                placeholder="you@example.com"
                                                aria-invalid={!!errors.email}
                                                aria-describedby={errors.email ? "error-email" : undefined}
                                                className={inputClass}
                                            />
                                            {errors.email && (
                                                <p id="error-email" data-testid="error-email" className="mt-2 text-xs text-red-700">
                                                    {errors.email}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="enquiry-service" className="mb-2 block text-sm font-medium text-ink">
                                                Service of interest <span className="text-bronze">*</span>
                                            </label>
                                            <select
                                                id="enquiry-service"
                                                data-testid="enquiry-service-select"
                                                value={form.service}
                                                onChange={update("service")}
                                                aria-invalid={!!errors.service}
                                                aria-describedby={errors.service ? "error-service" : undefined}
                                                className={inputClass}
                                            >
                                                <option value="">Select a service</option>
                                                {ENQUIRY_SERVICES.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.service && (
                                                <p id="error-service" data-testid="error-service" className="mt-2 text-xs text-red-700">
                                                    {errors.service}
                                                </p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="enquiry-location" className="mb-2 block text-sm font-medium text-ink">
                                                Location <span className="text-bronze">*</span>
                                            </label>
                                            <input
                                                id="enquiry-location"
                                                data-testid="enquiry-location-input"
                                                type="text"
                                                value={form.location}
                                                onChange={update("location")}
                                                placeholder="e.g. Anand Niketan, South Delhi"
                                                aria-invalid={!!errors.location}
                                                aria-describedby={errors.location ? "error-location" : undefined}
                                                className={inputClass}
                                            />
                                            {errors.location && (
                                                <p id="error-location" data-testid="error-location" className="mt-2 text-xs text-red-700">
                                                    {errors.location}
                                                </p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label htmlFor="enquiry-message" className="mb-2 block text-sm font-medium text-ink">
                                                Your requirement <span className="text-bronze">*</span>
                                            </label>
                                            <textarea
                                                id="enquiry-message"
                                                data-testid="enquiry-message-input"
                                                rows={5}
                                                value={form.message}
                                                onChange={update("message")}
                                                placeholder="A few lines about the property, space or furniture you have in mind."
                                                aria-invalid={!!errors.message}
                                                aria-describedby={errors.message ? "error-message" : undefined}
                                                className={`${inputClass} resize-y`}
                                            />
                                            {errors.message && (
                                                <p id="error-message" data-testid="error-message" className="mt-2 text-xs text-red-700">
                                                    {errors.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {serverError && (
                                        <p
                                            data-testid="enquiry-server-error"
                                            role="alert"
                                            className="mt-6 border border-red-700/30 bg-red-50 px-4 py-3 text-sm text-red-700"
                                        >
                                            {serverError}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        data-testid="enquiry-submit-btn"
                                        disabled={submitting}
                                        className="mt-8 inline-flex items-center justify-center bg-bronze px-8 py-3.5 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-bronze-light disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {submitting ? "Sending…" : "Submit enquiry"}
                                    </button>
                                </form>
                            )}
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
